import { Button } from 'antd';
import React, { useContext } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import type { ExtranetHotelRoom } from 'app/typings/core/extranet/hotel/rooms';

import { inventoryStore } from '../../../../InventoryStore';
import { useInventoryPermissions } from '../../../../hooks/useInventoryPermissions';
import { CollapseTable } from '../../../Table/CollapseTable/CollapseTable';

import { RoomBookingsRow } from './rows/RoomBookingsRow';
import { RoomDiscountPriceRow } from './rows/RoomDiscountPriceRow';
import { RoomDiscountRow } from './rows/RoomDiscountRow';
import { RoomForceUnpublishRow } from './rows/RoomForceUnpublishRow';
import { RoomPackPricesRow } from './rows/RoomPackPricesRow';
import { RoomPriceRow } from './rows/RoomPriceRow';
import { RoomStatusRow } from './rows/RoomStatusRow';
import { RoomStockRow } from './rows/RoomStockRow';
import { RoomStopSellRow } from './rows/RoomStopSellRow';

type Props = {
  room: ExtranetHotelRoom;
  categoryName?: string;
};

const RoomTableItemComponent: React.FC<Props> = ({ room, categoryName }) => {
  const { isEditMode, setBulkEditDrawerOpen } = useStore(
    inventoryStore,
    useShallow((state) => ({
      isEditMode: state.isEditMode,
      setBulkEditDrawerOpen: state.setRoomBulkEditDrawer,
    }))
  );
  const { hotelInfo } = useContext(HotelContext);
  const { canForceUnpublish, canAccessBulkEdit } = useInventoryPermissions();
  const roomName = room.name || categoryName || '';

  return (
    <CollapseTable
      key={`exp-${room.id}`}
      images={room.coverPicture ? [room.coverPicture] : undefined}
      title={roomName}
      keyPanel={`exp-${room.id}`}
      extraHeader={
        canAccessBulkEdit && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setBulkEditDrawerOpen(room.id, true);
            }}
            disabled={isEditMode}
          >
            Bulk edit
          </Button>
        )
      }
    >
      <RoomStatusRow roomId={room.id} />
      {hotelInfo?.stockScrapperEnabled && <RoomStopSellRow roomId={room.id} />}
      <RoomStockRow roomId={room.id} />
      <RoomBookingsRow roomId={room.id} />
      <RoomPriceRow roomId={room.id} />
      <RoomDiscountRow roomId={room.id} />
      <RoomDiscountPriceRow roomId={room.id} />
      {isEditMode && canForceUnpublish && (
        <RoomForceUnpublishRow roomId={room.id} />
      )}
      <RoomPackPricesRow roomId={room.id} roomName={roomName} />
    </CollapseTable>
  );
};

export const RoomTableItem = React.memo(RoomTableItemComponent);
